import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { AdminShell, adminPage } from '#/components/admin/admin-shell'
import { DataTable, KV, Pager, Panel, StatTile } from '#/components/admin/ui'
import { Badge } from '#/components/ui/badge'
import { useAdminTransactions, useAdminTxSummary, type AdminTransaction } from '#/lib/api-admin'
import { fmtCurrency, fmtDateTime, fmtNumber } from '#/lib/format'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/admin/transactions')({ component: adminPage(Page) })
const cur = (c: string | null) => (c === 'USD' ? 'USD' : 'IDR')

function Page() {
  const [page, setPage] = useState(1)
  const { data, isLoading, isError, isFetching, refetch } = useAdminTransactions(page)
  const { data: s } = useAdminTxSummary()
  return (
    <AdminShell title={m.admin_nav_transactions()} wide>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {(s?.byCurrency.length ? s.byCurrency : [{ currency: 'IDR', paidTotal: 0, count: 0, paid30d: 0 }]).map((c) => (
          <StatTile key={c.currency} label={m.admin_tx_paid_total({ currency: c.currency })} value={fmtCurrency(c.paidTotal, cur(c.currency))} sub={m.admin_tx_paid_sub({ n: fmtNumber(c.count), d30: fmtCurrency(c.paid30d, cur(c.currency)) })} />
        ))}
        <Panel title={m.admin_tx_by_status()} className="sm:col-span-2 lg:col-span-1">
          {s?.byStatus.length ? <KV items={s.byStatus.map((x) => ({ k: x.status, v: fmtNumber(x.count) }))} /> : <p className="text-sm text-muted-foreground">{m.admin_empty()}</p>}
        </Panel>
        <Panel title={m.admin_tx_by_plan()} className="sm:col-span-2 lg:col-span-1">
          {s?.byPlan.length ? <KV items={s.byPlan.map((x) => ({ k: x.name, v: `${fmtNumber(x.count)} · ${fmtCurrency(x.paidTotal, cur(x.currency))}` }))} /> : <p className="text-sm text-muted-foreground">{m.admin_empty()}</p>}
        </Panel>
      </div>
      <DataTable<AdminTransaction>
        rows={data?.transactions}
        loading={isLoading} error={isError} retrying={isFetching} onRetry={refetch}
        rowKey={(t) => t.id}
        cols={[
          { key: 'date', header: m.tx_col_date(), render: (t) => fmtDateTime(t.createdAt) },
          { key: 'order', header: m.admin_col_order(), render: (t) => <span className="font-mono text-xs">{t.orderId}</span> },
          { key: 'user', header: m.admin_col_email(), render: (t) => t.userEmail },
          { key: 'plan', header: m.tx_col_plan(), render: (t) => t.planName ?? '—' },
          { key: 'amount', header: m.tx_col_amount(), render: (t) => <div className="tabular-nums">{fmtCurrency(t.grossAmount - t.discountAmount, cur(t.currency))}{t.discountAmount ? <div className="text-xs text-muted-foreground">−{fmtCurrency(t.discountAmount, cur(t.currency))} {t.voucherCode}</div> : null}</div>, className: 'text-right' },
          { key: 'status', header: m.tx_col_status(), render: (t) => <Badge variant={t.status === 'paid' || t.status === 'settlement' ? 'default' : 'secondary'}>{t.status}</Badge> },
          { key: 'pay', header: m.admin_col_payment(), render: (t) => `${t.provider}${t.paymentType ? ` · ${t.paymentType}` : ''}` },
        ]}
      />
      {data ? <Pager page={data.page} total={data.total} pageSize={data.pageSize} onPage={setPage} /> : null}
    </AdminShell>
  )
}
